import { Loader as GoogleMapsLoader } from '@googlemaps/js-api-loader';

let loaderInstance: GoogleMapsLoader | null = null;
let isInitialized = false;

let AdvancedMarkerElement: typeof google.maps.marker.AdvancedMarkerElement;
let PinElement: typeof google.maps.marker.PinElement;
let GeocoderConstructor: typeof google.maps.Geocoder;

export const useMaps = async (googleMapsEmbedApiKey: string) => {
  if (!loaderInstance) {
    loaderInstance = new GoogleMapsLoader({
      apiKey: googleMapsEmbedApiKey,
      version: 'weekly',
      libraries: ['core', 'maps', 'marker', 'geocoding'],
    });
  }

  if (!isInitialized) {
    const [markerLib, geocodingLib] = await Promise.all([
      loaderInstance.importLibrary('marker'),
      loaderInstance.importLibrary('geocoding'),
    ]);

    AdvancedMarkerElement = markerLib.AdvancedMarkerElement;
    PinElement = markerLib.PinElement;
    GeocoderConstructor = geocodingLib.Geocoder;

    isInitialized = true;
  }

  const geocoder = new GeocoderConstructor();

  /**
     * Get the geocoded coordinates from the Maps API.
     * @param address Address object that we want to map
     * @returns LatLng object containing the address's coordinates
     */
  const geocodeAddress = async (address: Address): Promise<google.maps.LatLng | null> => {
    try {
      const response = await geocoder.geocode({
        address: `${address.number} ${address.street}, ${address.postal_community}, ${address.state} ${address.postal_code}`,
      });

      if (!response.results.length) {
        console.warn('Could not get coordinates for address');
        return null;
      }

      const { location } = response.results[0].geometry;
      return new google.maps.LatLng(location.lat(), location.lng());
    }
    catch (error) {
      console.error('Geocoding error:', error);
      return null;
    }
  };

  /**
     * Return the coordinates of an address. By default, we will return coordinates already stored on the Address.
     * If the address does not have coordinate value, or by setting the encodeAddress flag, we will retrieve the
     * coordinates for the address from the Maps API.
     * @param address
     * @param encodeAddress
     */
  const getCoordinatesFromAddress = async (address: Address, newCoordinatesFromApi: boolean): Promise<google.maps.LatLng | null> => {
    if (newCoordinatesFromApi || !address.coordinates) {
      const position = await geocodeAddress(address);
      if (position) {
        address.coordinates = `${position.lat()},${position.lng()}`;
      }
      return position;
    }

    const [lat, lng] = address.coordinates.split(',').map(Number);
    if (Number.isNaN(lat) || Number.isNaN(lng)) {
      console.error('Invalid coordinate string');
      return null;
    }

    return new google.maps.LatLng(lat, lng);
  };

  const getAddressFromCoordinates = async (lat: number, lng: number): Promise<PartialAddress | null> => {
    try {
      const response = await geocoder.geocode({ location: { lat, lng } });

      if (!response.results[0]) {
        return null;
      }

      const components = response.results[0].address_components;
      const getComponent = (type: string) =>
        components.find(c => c.types.includes(type))?.long_name || '';

      return {
        street: getComponent('route'),
        number: getComponent('street_number'),
        postal_community: getComponent('locality'),
        postal_code: getComponent('postal_code'),
        state: getComponent('administrative_area_level_1'), // fixed type
      };
    }
    catch (err) {
      console.error('Reverse geocoding failed:', err);
      return null;
    }
  };

  const createFirePin = () => {
    return new PinElement({
      background: 'yellow',
      glyph: '🔥',
    });
  };

  const createPersonPin = () => {
    return new PinElement({
      background: 'red',
      glyph: '👤',
    });
  };

  return {
    AdvancedMarkerElement,
    PinElement,
    Geocoder: GeocoderConstructor,
    createFirePin,
    getCoordinatesFromAddress,
    getAddressFromCoordinates,
  };
};
